import { useState, useEffect } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Minus, Plus, CreditCard } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { billingApi, TopUpConfigResponse, PaymentMethod } from '@/lib/api/billing';
import { useEmbedStore } from '@/stores/embed-store';
import toast from 'react-hot-toast';

interface TopUpCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface PaymentResult {
  credits: number;
  subtotal: number;
  tax: number;
  total: number;
  cardLabel: string;
}

export function TopUpCreditsModal({ isOpen, onClose, onSuccess }: TopUpCreditsModalProps) {
  const { getIdToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [config, setConfig] = useState<TopUpConfigResponse | null>(null);
  const [credits, setCredits] = useState(50);
  const [card, setCard] = useState<PaymentMethod | null>(null);
  const [result, setResult] = useState<PaymentResult | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadData();
      setResult(null);
    }
  }, [isOpen]);

  const loadData = async (): Promise<void> => {
    setConfigLoading(true);
    setCard(null);
    try {
      const token = await getIdToken();
      const [configData, pmResponse] = await Promise.all([
        billingApi.getTopUpConfig(),
        token ? billingApi.getPaymentMethods(token).catch(() => null) : null,
      ]);

      setConfig(configData);
      setCredits(configData.minCredits);

      if (pmResponse?.paymentMethods?.length) {
        const invoiceDefault = pmResponse.paymentMethods.find(pm => pm.isInvoiceDefault);
        const subscriptionDefault = pmResponse.paymentMethods.find(pm => pm.isSubscriptionDefault);
        setCard(invoiceDefault || subscriptionDefault || pmResponse.paymentMethods[0]);
      }
    } catch (error: unknown) {
      console.error('Failed to load top-up data:', error);
      toast.error('Failed to load pricing');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleIncrement = (): void => {
    if (config) setCredits((prev) => prev + config.stepCredits);
  };

  const handleDecrement = (): void => {
    if (config && credits > config.minCredits) setCredits((prev) => prev - config.stepCredits);
  };

  const calculatePrice = (): number => {
    if (!config) return 0;
    return (credits * config.pricePerCreditCents) / 100;
  };

  const formatBrand = (brand: string): string => {
    const brands: Record<string, string> = {
      visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex',
      discover: 'Discover', diners: 'Diners', jcb: 'JCB', unionpay: 'UnionPay',
    };
    return brands[brand] || brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const formatCents = (cents: number): string => (cents / 100).toFixed(2);

  const cardLabel = card
    ? `${formatBrand(card.brand)} •••• ${card.last4}`
    : '';

  const handlePurchase = async (): Promise<void> => {
    setLoading(true);
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      if (card) {
        const response = await billingApi.chargeTopUp(token, credits, card.id);
        const subtotal = calculatePrice() * 100;
        setResult({
          credits,
          subtotal,
          tax: response.tax,
          total: response.amountCharged,
          cardLabel,
        });
        onSuccess?.();
      } else {
        const { url } = await billingApi.topUpCredits(token, credits);
        if (useEmbedStore.getState().isEmbedded) {
          window.open(url, '_blank');
          toast.success('Checkout opened in new tab');
          onClose();
        } else {
          window.location.href = url;
        }
      }
    } catch (error: unknown) {
      const axiosErr = error as { response?: { data?: { message?: string } } };
      console.error('Failed to process top-up:', error);
      toast.error(axiosErr.response?.data?.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = (): void => {
    setResult(null);
    onClose();
  };

  const priceLabel = `$${calculatePrice().toFixed(2)}`;

  return (
    <Dialog open={isOpen} onOpenChange={result ? handleDone : onClose}>
      <DialogContent className="max-w-[90vw] w-full sm:max-w-[420px] rounded-xl p-0 gap-0" onOpenAutoFocus={(e) => e.preventDefault()}>

        {/* Success state */}
        {result ? (
          <div className="px-6 py-8">
            <div className="text-center mb-6">
              <p className="text-sm font-medium text-muted-foreground">Payment Confirmed</p>
              <p className="text-xl font-semibold mt-1">{result.credits} credits added</p>
            </div>

            <div className="border-t border-b border-border py-4 space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular-nums">${formatCents(result.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax</span>
                <span className="tabular-nums">${formatCents(result.tax)}</span>
              </div>
              <div className="flex justify-between text-sm font-medium">
                <span>Charged</span>
                <span className="tabular-nums">${formatCents(result.total)}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4 mb-6">
              <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{result.cardLabel}</span>
            </div>

            <Button
              onClick={handleDone}
              className="w-full h-12 text-sm font-medium bg-foreground hover:bg-foreground/90 text-background"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="px-6 pt-6 pb-2">
              <DialogTitle className="text-lg">Top Up Credits</DialogTitle>
              <DialogDescription className="text-sm">
                Purchase additional credits for your workspace
              </DialogDescription>
            </DialogHeader>

            {configLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : config ? (
              <div className="px-6 pb-6">
                {/* Credit stepper */}
                <div className="flex items-center justify-center gap-4 py-4">
                  <button
                    onClick={handleDecrement}
                    disabled={credits <= config.minCredits || loading}
                    className="h-11 w-11 rounded-full border border-border bg-background hover:bg-muted flex items-center justify-center text-foreground/60 disabled:opacity-30 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <div className="text-center min-w-[88px]">
                    <span className="text-4xl font-bold tabular-nums">{credits}</span>
                    <p className="text-xs text-muted-foreground mt-0.5">credits</p>
                  </div>
                  <button
                    onClick={handleIncrement}
                    disabled={loading}
                    className="h-11 w-11 rounded-full border border-border bg-background hover:bg-muted flex items-center justify-center text-foreground/60 disabled:opacity-30 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                {/* Payment method */}
                {card && (
                  <div className="flex items-center justify-between mt-2 mb-6">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {cardLabel} · {String(card.expMonth).padStart(2, '0')}/{String(card.expYear).slice(-2)}
                      </span>
                    </div>
                    <button
                      onClick={async () => {
                        const token = await getIdToken();
                        if (!token) return;
                        try {
                          const { url } = await billingApi.createPortalSession(token);
                          window.open(url, '_blank');
                        } catch {
                          toast.error('Failed to open billing portal');
                        }
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                )}

                {/* CTA */}
                <Button
                  onClick={handlePurchase}
                  disabled={loading}
                  className="w-full h-12 text-sm font-medium bg-foreground hover:bg-foreground/90 text-background"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    `Pay ${priceLabel} + tax`
                  )}
                </Button>

                {/* Fine print */}
                <p className="text-[11px] text-muted-foreground/70 text-center mt-3">
                  Credits never expire · Used after plan credits run out
                </p>
              </div>
            ) : (
              <div className="py-12 text-center text-sm text-muted-foreground">
                Unable to load pricing. Please try again.
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
